import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    Chip,
    IconButton,
    Tooltip,
} from "@mui/material";

import RefreshIcon from "@mui/icons-material/Refresh";
import DeleteIcon from "@mui/icons-material/Delete";

import { useUser } from "../../components/UserContext";
import { useApiClient } from "../../components/useApiClient";

import type { UserRole, UserAccessDto, AccessScope } from "../../models/UserProfile";
import type { UserInviteRequest } from "../../models/UserInvite";

export interface AccessDialogProps {
    open: boolean;
    onClose: () => void;
    scope: AccessScope;
    title?: string;
    onChanged?: () => Promise<void> | void;
}

function isValidEmail(email: string) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

const UserManagementAccessDialog: React.FC<AccessDialogProps> = ({ open, onClose, scope, title, onChanged }) => {
    const { getAccessToken, isAdmin, isOwner } = useUser();
    const { get, post, postJson, put, delete: del } = useApiClient();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [rows, setRows] = useState<UserAccessDto[]>([]);
    const [roles, setRoles] = useState<UserRole[]>([]);

    const [inviteEmail, setInviteEmail] = useState("");
    const [inviteRoleId, setInviteRoleId] = useState<number | "">("");

    const [error, setError] = useState<string | null>(null);
    const [status, setStatus] = useState<string>("");

    const canManage = useMemo(() => isAdmin || isOwner, [isAdmin, isOwner]);

    const inviteRows = useMemo(() => rows.filter(r => r.kind === "Invite"), [rows]);

    const dialogTitle = useMemo(() => {
        if (title) return title;
        return scope.kind === "client" ? "Client Access" : "Portfolio Access";
    }, [scope, title]);

    const accessUrl = useMemo(() => {
        return scope.kind === "client"
            ? `clients/${scope.clientId}/access`
            : `portfolios/${scope.portfolioId}/access`;
    }, [scope]);

    const updateUserUrl = useMemo(() => {
        return scope.kind === "client"
            ? `clients/${scope.clientId}/users`
            : `portfolios/${scope.portfolioId}/users`;
    }, [scope]);

    const loadAsync = async () => {
        if (!open || !canManage) return;

        setError(null);
        setStatus("");
        setLoading(true);

        try {
            const token = await getAccessToken();
            if (!token) throw new Error("Invalid access token");

            const roleList = await get<UserRole[]>("user/roles", token);
            setRoles(roleList);

            const access = await get<UserAccessDto[]>(accessUrl, token);
            setRows(access);
        } catch (e: any) {
            setError(e?.response?.data?.detail ?? e?.message ?? "Failed to load access.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!open) return;
        loadAsync();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, accessUrl, canManage]);

    const handleInviteAsync = async () => {
        setError(null);
        setStatus("");

        const email = inviteEmail.trim();
        if (!isValidEmail(email)) {
            setError("Please enter a valid email address.");
            return;
        }
        if (!inviteRoleId) {
            setError("Please select a role.");
            return;
        }

        setSaving(true);
        try {
            const token = await getAccessToken();
            if (!token) throw new Error("Invalid access token");

            const dto: UserInviteRequest = {
                email,
                clientId: scope.clientId,
                roleId: inviteRoleId,
                portfolioId: scope.kind === "portfolio" ? scope.portfolioId : undefined,
            };

            await postJson("invitations", token, JSON.stringify(dto));

            setInviteEmail("");
            setInviteRoleId("");
            setStatus("User added / invitation sent.");
            await loadAsync();
            await onChanged?.();
        } catch (e: any) {
            setError(e?.response?.data?.detail ?? e?.message ?? "Failed to invite user.");
        } finally {
            setSaving(false);
        }
    };

    async function handleChangeUserRoleAsync(userId: number, roleId: number) {
        try {
            const token = await getAccessToken();
            if (!token) throw new Error("Invalid access token");

            const formData = new FormData();
            formData.append("UserId", userId.toString());
            formData.append("RoleId", roleId.toString());
            formData.append("IsActive", "true");

            await put(updateUserUrl, token, formData);

            await loadAsync();
            await onChanged?.();
        } catch (e: any) {
            setError(e?.response?.data?.detail ?? e?.message ?? "Failed to update user role.");
        }
    }

    async function handleRevokeInviteAsync(inviteId: string) {
        try {
            const token = await getAccessToken();
            if (!token) throw new Error("Invalid access token");

            await del(`invitations/${inviteId}`, token);

            await loadAsync();
            await onChanged?.();
        } catch (e: any) {
            setError(e?.response?.data?.detail ?? e?.message ?? "Failed to revoke invite.");
        }
    }

    async function handleRefreshInviteAsync(inviteId: string) {
        try {
            const token = await getAccessToken();
            if (!token) throw new Error("Invalid access token");

            await post(`invitations/${inviteId}/refresh`, token, new FormData());

            await loadAsync();
            await onChanged?.();
        } catch (e: any) {
            setError(e?.response?.data?.message ?? e?.response?.data?.detail ?? e?.message ?? "Failed to refresh invite.");
        }
    }

    const roleNameById = useMemo(() => {
        const m = new Map<number, string>();
        roles.forEach(r => m.set(r.id, r.role));
        return m;
    }, [roles]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle>{dialogTitle}</DialogTitle>
            <DialogContent dividers>
                {!canManage ? (
                    <Typography>You are not authorised to manage access.</Typography>
                ) : (
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {error && (
                            <Box sx={{ border: "1px solid #ddd", p: 1.5 }}>
                                {error}
                            </Box>
                        )}
                        {status && (
                            <Box sx={{ border: "1px solid #ddd", p: 1.5 }}>
                                {status}
                            </Box>
                        )}

                        {/* Invite new user */}
                        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
                            <TextField
                                label="Invite email"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                size="small"
                                sx={{ minWidth: 260 }}
                            />
                            <FormControl size="small" sx={{ minWidth: 200 }}>
                                <InputLabel id="invite-role-label">Role</InputLabel>
                                <Select
                                    labelId="invite-role-label"
                                    label="Role"
                                    value={inviteRoleId}
                                    onChange={(e) => setInviteRoleId(Number(e.target.value))}
                                >
                                    {roles.map(r => (
                                        <MenuItem key={r.id} value={r.id}>{r.role}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Button
                                variant="outlined"
                                onClick={handleInviteAsync}
                                disabled={!inviteEmail.trim() || inviteRoleId === "" || loading || saving}
                            >
                                Send invite
                            </Button>
                        </Box>

                        {/* Access list */}
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Name</TableCell>
                                    <TableCell>Scope</TableCell>
                                    <TableCell>Portfolio</TableCell>
                                    <TableCell>Role</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r) => {
                                    const isUser = r.kind === "User";
                                    const isInvite = r.kind === "Invite";
                                    const key = isUser ? `u:${r.userId}` : `i:${r.inviteId}`;

                                    const statusChip = isUser
                                        ? <Chip size="small" label={r.isActive ? "Active" : "Inactive"} />
                                        : <Chip size="small" label={r.isExpired ? "Expired invite" : "Pending invite"} />;

                                    const scopeChip = (
                                        <Chip
                                            size="small"
                                            label={r.scope === "Client" ? "Client-wide" : "Portfolio"}
                                            variant={r.scope === "Client" ? "outlined" : "filled"}
                                        />
                                    );

                                    // Editing rules:
                                    // - In portfolio dialog: allow editing portfolio-scoped users
                                    // - In client dialog: allow editing client-wide users only (portfolio rows read-only)
                                    const canEditRole =
                                        isUser &&
                                        ((scope.kind === "portfolio" && r.scope === "Portfolio") ||
                                            (scope.kind === "client" && r.scope === "Client")) &&
                                        !!r.userId;

                                    return (
                                        <TableRow key={key}>
                                            <TableCell>{r.email}</TableCell>
                                            <TableCell>{r.displayName ?? "—"}</TableCell>
                                            <TableCell>{scopeChip}</TableCell>
                                            <TableCell>{r.portfolioName ?? "—"}</TableCell>
                                            <TableCell>
                                                {canEditRole ? (
                                                    <Select
                                                        value={r.roleId}
                                                        onChange={(e) => handleChangeUserRoleAsync(r.userId!, Number(e.target.value))}
                                                        size="small"
                                                    >
                                                        {roles.map(role => (
                                                            <MenuItem key={role.id} value={role.id}>
                                                                {role.role}
                                                            </MenuItem>
                                                        ))}
                                                    </Select>
                                                ) : (
                                                    <span>{r.role ?? roleNameById.get(r.roleId) ?? ""}</span>
                                                )}
                                            </TableCell>
                                            <TableCell>{statusChip}</TableCell>
                                            <TableCell align="right">
                                                {isInvite && r.inviteId && (
                                                    <>
                                                        <Tooltip title="Refresh & resend">
                                                            <IconButton size="small" onClick={() => handleRefreshInviteAsync(r.inviteId!)}>
                                                                <RefreshIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Revoke invite">
                                                            <IconButton size="small" onClick={() => handleRevokeInviteAsync(r.inviteId!)}>
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>

                        {!!inviteRows.length && (
                            <Typography variant="caption" sx={{ opacity: 0.75 }}>
                                Invites remain visible until accepted or revoked. Expired invites can be refreshed and resent.
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

export default UserManagementAccessDialog;